import { useRef, useState, useEffect } from "react";
import { Play, Pause, Volume2, VolumeX, RotateCcw, RotateCw, Download } from "lucide-react";

const SPEEDS = [0.75, 1, 1.25, 1.5, 2];

function formatTime(s) {
  if (!isFinite(s) || s < 0) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60).toString().padStart(2, "0");
  return `${m}:${sec}`;
}

/**
 * Wraps a single native <audio> element — same src, same file serving, same backend.
 * This is purely a richer UI control surface over the standard HTMLMediaElement API
 * (play/pause/currentTime/volume/playbackRate), not a replacement for how audio is
 * stored or served.
 */
export default function PodcastAudioPlayer({ src, downloadName }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [current, setCurrent] = useState(0);
  const [duration, setDuration] = useState(0);
  const [speed, setSpeed] = useState(1);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    const onTime = () => setCurrent(el.currentTime);
    const onLoaded = () => setDuration(el.duration || 0);
    const onEnd = () => setPlaying(false);
    el.addEventListener("timeupdate", onTime);
    el.addEventListener("loadedmetadata", onLoaded);
    el.addEventListener("ended", onEnd);
    return () => {
      el.removeEventListener("timeupdate", onTime);
      el.removeEventListener("loadedmetadata", onLoaded);
      el.removeEventListener("ended", onEnd);
    };
  }, [src]);

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) el.pause();
    else el.play();
    setPlaying(!playing);
  };

  const seek = (e) => {
    const el = audioRef.current;
    if (!el || !duration) return;
    const pct = e.target.value / 100;
    el.currentTime = pct * duration;
    setCurrent(el.currentTime);
  };

  const skip = (secs) => {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = Math.min(Math.max(el.currentTime + secs, 0), duration || 0);
  };

  const changeSpeed = () => {
    const idx = SPEEDS.indexOf(speed);
    const next = SPEEDS[(idx + 1) % SPEEDS.length];
    setSpeed(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  const changeVolume = (e) => {
    const v = parseFloat(e.target.value);
    setVolume(v);
    setMuted(v === 0);
    if (audioRef.current) audioRef.current.volume = v;
  };

  const toggleMute = () => {
    const el = audioRef.current;
    if (!el) return;
    el.muted = !muted;
    setMuted(!muted);
  };

  const pct = duration ? (current / duration) * 100 : 0;

  return (
    <div
      className="bg-white"
      style={{ borderRadius: 24, border: "1px solid #E2E8F0", boxShadow: "0 10px 25px rgba(0,0,0,0.05)", padding: 20 }}
      data-testid="podcast-audio-player"
    >
      <audio ref={audioRef} src={src} preload="metadata" />

      <div className="flex items-center gap-4">
        <button
          onClick={togglePlay}
          aria-label={playing ? "Pause" : "Play"}
          data-testid="audio-play-toggle"
          className="w-12 h-12 shrink-0 rounded-full grid place-items-center text-white transition-transform hover:scale-105"
          style={{ backgroundColor: "#3B68E7" }}
        >
          {playing ? <Pause className="w-5 h-5" fill="currentColor" /> : <Play className="w-5 h-5 ml-0.5" fill="currentColor" />}
        </button>

        <div className="flex-1 min-w-0">
          <input
            type="range"
            min={0}
            max={100}
            step={0.1}
            value={pct}
            onChange={seek}
            aria-label="Seek"
            data-testid="audio-seek"
            className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
            style={{
              background: `linear-gradient(to right, #3B68E7 ${pct}%, #E2E8F0 ${pct}%)`,
              accentColor: "#3B68E7",
            }}
          />
          <div className="flex justify-between mt-1.5 text-[11px]" style={{ color: "#94A3B8" }}>
            <span>{formatTime(current)}</span>
            <span>{formatTime(duration)}</span>
          </div>
        </div>

        <div className="hidden sm:flex items-center gap-1 shrink-0">
          <button onClick={() => skip(-15)} aria-label="Back 15 seconds" className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-50" style={{ color: "#64748B" }}>
            <RotateCcw className="w-4 h-4" />
          </button>
          <button onClick={() => skip(15)} aria-label="Forward 15 seconds" className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-50" style={{ color: "#64748B" }}>
            <RotateCw className="w-4 h-4" />
          </button>
          <button
            onClick={changeSpeed}
            aria-label="Playback speed"
            data-testid="audio-speed"
            className="text-xs font-semibold w-10 h-8 rounded-full hover:bg-gray-50"
            style={{ color: "#3B68E7" }}
          >
            {speed}×
          </button>
          <button onClick={toggleMute} aria-label={muted ? "Unmute" : "Mute"} className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-50" style={{ color: "#64748B" }}>
            {muted || volume === 0 ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
          </button>
          <input
            type="range" min={0} max={1} step={0.05}
            value={muted ? 0 : volume}
            onChange={changeVolume}
            aria-label="Volume"
            className="w-16 h-1 rounded-full appearance-none cursor-pointer"
            style={{ accentColor: "#3B68E7" }}
          />
          {src && (
            <a href={src} download={downloadName} aria-label="Download episode" className="w-8 h-8 grid place-items-center rounded-full hover:bg-gray-50" style={{ color: "#64748B" }}>
              <Download className="w-4 h-4" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
}
